import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Shield, Lock, Eye, Users, FileText, Mail, MapPin, Phone, CheckCircle, Database, AlertTriangle, Clock } from 'lucide-react';
import LegalPageShell, { LegalFeatureCard } from '../components/legal/LegalPageShell';
import { glassCard, glassCardHover } from '../utils/glassStyles';

export default function PrivacyPolicy() {
 const privacyFeatures = [
 {
 icon: Shield,
 title: "Data Protection",
 description: "Compliant with Ethiopian Data Protection Proclamation"
 },
 {
 icon: Lock,
 title: "Secure Encryption",
 description: "Military-grade encryption for all personal data"
 },
 {
 icon: Eye,
 title: "Transparency",
 description: "Full control over your data and visibility"
 },
 {
 icon: Users,
 title: "Your Rights",
 description: "Access, correct, or delete your data anytime"
 }
 ];

 const userRights = [
 "Right to access your personal data",
 "Right to correct inaccurate information",
 "Right to request data deletion",
 "Right to object to data processing",
 "Right to data portability",
 "Right to withdraw consent"
 ];

 const dataCollection = [
 { type: "Personal Information", items: ["Name, email, phone number", "Profile photo, resume", "Employment history"] },
 { type: "Usage Data", items: ["IP address, browser type", "Page visits and interactions", "Device information"] },
 { type: "Cookies & Tracking", items: ["Session cookies", "Preference cookies", "Analytics data"] }
 ];

 return (
 <LegalPageShell
 title="Privacy Policy"
 subtitle="Your privacy is our priority. Learn how Shebeka collects, uses, and protects your personal information."
 icon={Shield}
 badges={[
 { label: "GDPR Compliant", className: "bg-green-500/10 text-green-700 dark:text-green-300" },
 { label: "Ethiopian Data Protection", className: "bg-blue-500/10 text-blue-700 dark:text-blue-300" },
 { label: "Last Updated: Jan 4, 2025", className: "bg-accent/10 text-primary dark:text-accent" },
 ]}
 relatedLinks={[
 { to: "/cookie-policy", label: "Cookie Policy" },
 { to: "/terms", label: "Terms of Service" },
 { to: "/contact", label: "Contact Us" },
 ]}
 >
 {/* Privacy Features Grid */}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
 {privacyFeatures.map((feature) => (
 <LegalFeatureCard key={feature.title} {...feature} />
 ))}
 </div>

 {/* Introduction */}
 <Card className={`mb-8 sm:mb-12 border-primary/20 ${glassCard}`}>
 <CardContent className="p-4 sm:p-6 md:p-8">
 <div className="flex flex-col sm:flex-row items-start gap-4">
 <div className="flex-shrink-0">
 <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
 </div>
 <div>
 <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed mb-4">
 Welcome to <span className="font-bold text-primary">Shebeka's</span> Privacy Policy. This document outlines how we collect, use, disclose, and safeguard your personal information when you use our job portal platform.
 </p>
 <p className="text-sm sm:text-base text-muted-foreground">
 By using Shebeka, you agree to the collection and use of information in accordance with this policy. We are committed to protecting your privacy and ensuring transparency in our data practices.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Information We Collect */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Database className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 Information We Collect
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
 {dataCollection.map((category) => (
 <Card key={category.type} className={glassCardHover}>
 <CardHeader>
 <CardTitle className="text-base sm:text-lg">{category.type}</CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {category.items.map((item, idx) => (
 <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
 <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
 <span>{item}</span>
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>

 {/* How We Use Your Information */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Lock className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 How We Use Your Information
 </h2>
 <Card className={glassCard}>
 <CardContent className="p-4 sm:p-6">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {[
 "To create and manage your account",
 "To process job applications",
 "To match job seekers with opportunities",
 "To communicate about platform updates",
 "To improve our services and user experience",
 "To comply with legal obligations",
 "To prevent fraud and ensure security",
 "To provide customer support"
 ].map((item, idx) => (
 <div key={idx} className="flex items-start gap-3 p-3 rounded-lg hover: transition-colors">
 <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
 <span className="text-sm sm:text-base text-foreground">{item}</span>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Your Rights */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Users className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 Your Privacy Rights
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {userRights.map((right, idx) => (
 <Card key={idx} className="hover:shadow-md transition-shadow border-border">
 <CardContent className="p-4 sm:p-5">
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
 <span className="text-sm font-bold text-primary">{idx + 1}</span>
 </div>
 <p className="text-sm sm:text-base text-foreground font-medium">{right}</p>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>

 {/* Data Security */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 Data Security Measures
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <Card className={glassCard}>
 <CardHeader>
 <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
 <Lock className="w-5 h-5 text-primary" />
 Technical Safeguards
 </CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {[
 "End-to-end encryption for sensitive data",
 "Secure HTTPS connection for all communications",
 "Regular security audits and penetration testing",
 "Firewalls and intrusion detection systems"
 ].map((item, idx) => (
 <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
 <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
 {item}
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>

 <Card className={glassCard}>
 <CardHeader>
 <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
 <Users className="w-5 h-5 text-primary" />
 Organizational Safeguards
 </CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {[
 "Strict access controls and authentication",
 "Employee training on data protection",
 "Regular privacy impact assessments",
 "Incident response and breach notification procedures"
 ].map((item, idx) => (
 <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
 <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
 {item}
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>
 </div>
 </div>

 {/* Data Retention */}
 <Card className="mb-8 sm:mb-12 bg-yellow-500/10 border-yellow-200 dark:border-yellow-800/30">
 <CardContent className="p-4 sm:p-6">
 <div className="flex items-start gap-4">
 <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-1" />
 <div>
 <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2">Data Retention Policy</h3>
 <p className="text-sm sm:text-base text-muted-foreground">
 We retain your personal data only for as long as necessary to fulfill the purposes outlined in this privacy policy, comply with legal obligations, resolve disputes, and enforce our agreements. You may request deletion of your account and data at any time.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Contact DPO */}
 <Card className={`border-border shadow-lg ${glassCard}`}>
 <CardHeader>
 <CardTitle className="text-xl sm:text-2xl text-center">Contact Our Data Protection Officer</CardTitle>
 <CardDescription className="text-center">
 For privacy-related inquiries, concerns, or to exercise your rights
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
 <div className="flex flex-col items-center text-center p-4 rounded-lg ">
 <Mail className="w-8 h-8 text-primary mb-3" />
 <p className="text-xs text-muted-foreground mb-1">Email</p>
 <a href="mailto:privacy@shebeka.com" className="text-sm sm:text-base font-medium text-primary hover:underline">
 privacy@shebeka.com
 </a>
 </div>
 <div className="flex flex-col items-center text-center p-4 rounded-lg ">
 <Phone className="w-8 h-8 text-primary mb-3" />
 <p className="text-xs text-muted-foreground mb-1">Phone</p>
<a href="tel:+251962333331" className="text-sm sm:text-base font-medium text-primary hover:underline">
+251 96 233 3331
 </a>
 </div>
 <div className="flex flex-col items-center text-center p-4 rounded-lg ">
 <MapPin className="w-8 h-8 text-primary mb-3" />
 <p className="text-xs text-muted-foreground mb-1">Address</p>
 <p className="text-sm sm:text-base font-medium text-foreground">
 Addis Ababa, Ethiopia
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 <Card className="bg-gradient-to-r from-primary to-accent border-0 text-white">
 <CardContent className="p-6 sm:p-8">
 <div className="flex flex-col md:flex-row items-center justify-between gap-4">
 <div>
 <h3 className="text-lg sm:text-xl font-bold mb-2">Your Trust is Our Priority</h3>
 <p className="text-sm sm:text-base text-white/90">
 We are committed to protecting your privacy and maintaining the highest standards of data protection.
 </p>
 </div>
 <div className="text-xs sm:text-sm bg-white/20 backdrop-blur-sm rounded-xl px-3 sm:px-4 py-2 text-center">
 <Clock className="w-5 h-5 mx-auto mb-1" />
 <p className="font-bold">Updated Regularly</p>
 <p className="text-white/80">Last: Jan 4, 2025</p>
 </div>
 </div>
 </CardContent>
 </Card>
 </LegalPageShell>
 );
}
